/**
 * Phone Input Component
 *
 * Phone number input with country code picker.
 * - Country picker dropdown with search
 * - Text-based country code display (works consistently across all OS)
 * - Only allows digits in the phone number field
 * - Default country: Germany
 */

import { useState, useRef, useEffect, forwardRef, useMemo } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  COUNTRY_CODES,
  PRIORITY_COUNTRY_CODES,
  ALL_COUNTRY_CODES,
  getCountryByCode,
  filterPhoneInput,
  parsePhoneNumber,
  formatPhoneWithCountry,
  type CountryCode,
} from '@/lib/validation';

export interface PhoneInputProps {
  label?: string;
  value: string;
  onChange: (fullPhoneNumber: string) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ label, value, onChange, error, placeholder = 'Telefonnummer', required, disabled, className }, ref) => {
    // Parse initial value to get country and local number
    const parsed = parsePhoneNumber(value);
    const [selectedCountry, setSelectedCountry] = useState<string>(parsed.countryCode);
    const [localNumber, setLocalNumber] = useState<string>(parsed.localNumber);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Update local state when value prop changes
    useEffect(() => {
      if (value) {
        const parsed = parsePhoneNumber(value);
        setSelectedCountry(parsed.countryCode);
        setLocalNumber(parsed.localNumber);
      } else {
        setSelectedCountry('DE');
        setLocalNumber('');
      }
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsDropdownOpen(false);
          setSearchQuery('');
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
      if (isDropdownOpen && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isDropdownOpen]);

    // Filter countries based on search query
    const filteredCountries = useMemo(() => {
      if (!searchQuery.trim()) {
        // No search: show priority countries first, then divider, then all others
        return COUNTRY_CODES;
      }

      const query = searchQuery.toLowerCase().trim();
      return ALL_COUNTRY_CODES.filter(
        (country) =>
          country.name.toLowerCase().includes(query) ||
          country.code.toLowerCase().includes(query) ||
          country.dialCode.includes(query)
      );
    }, [searchQuery]);

    // Check if we should show the divider (priority section)
    const showPriorityDivider = !searchQuery.trim() && PRIORITY_COUNTRY_CODES.length > 0;

    const handleCountrySelect = (country: CountryCode) => {
      setSelectedCountry(country.code);
      setIsDropdownOpen(false);
      setSearchQuery('');
      // Update parent with new full phone number
      const fullNumber = formatPhoneWithCountry(country.code, localNumber);
      onChange(fullNumber);
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Filter to only allow digits
      const filtered = filterPhoneInput(e.target.value);
      setLocalNumber(filtered);
      // Update parent with full phone number
      const fullNumber = formatPhoneWithCountry(selectedCountry, filtered);
      onChange(fullNumber);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        setIsDropdownOpen(false);
        setSearchQuery('');
      } else if (e.key === 'Enter' && filteredCountries.length > 0) {
        handleCountrySelect(filteredCountries[0]);
      }
    };

    const currentCountry = getCountryByCode(selectedCountry);
    const inputId = label ? label.toLowerCase().replace(/\s+/g, '-') : undefined;

    return (
      <div className={cn('w-full', className)}>
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-slate-300 mb-1.5">
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}

        <div className="flex">
          {/* Country Code Picker */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
              disabled={disabled}
              className={cn(
                'flex items-center gap-1.5 px-3 h-11 rounded-l-xl border-r-0 text-sm',
                'bg-slate-800/80 border border-slate-600/50 text-slate-100',
                'hover:bg-slate-700/80 hover:border-slate-500/50 transition-all duration-200 min-w-25',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                error && 'border-red-500/50'
              )}
            >
              <span className="text-slate-400 font-medium text-xs">{currentCountry?.code}</span>
              <span className="text-slate-300 font-medium">{currentCountry?.dialCode}</span>
              <ChevronDown className={cn('w-4 h-4 text-slate-400 transition-transform ml-auto', isDropdownOpen && 'rotate-180')} />
            </button>

            {/* Dropdown with Search */}
            {isDropdownOpen && (
              <div className="absolute z-50 top-full left-0 mt-1 w-72 bg-slate-800 border border-slate-600/50 rounded-xl shadow-xl overflow-hidden">
                {/* Search Input */}
                <div className="p-2 border-b border-slate-600">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      placeholder="Land suchen..."
                      autoComplete="off"
                      className={cn(
                        'w-full pl-8 pr-8 py-2 text-sm rounded-lg',
                        'bg-slate-700/80 border border-slate-600/50 text-slate-100',
                        'placeholder:text-slate-500',
                        'focus:outline-none focus:ring-1 focus:ring-orange-500/50 focus:border-orange-500/50'
                      )}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-600 rounded"
                      >
                        <X className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Country List */}
                <div className="max-h-60 overflow-y-auto">
                  {filteredCountries.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-slate-400 text-center">
                      Kein Land gefunden
                    </div>
                  ) : (
                    <>
                      {/* Priority countries section */}
                      {showPriorityDivider && (
                        <>
                          {PRIORITY_COUNTRY_CODES.map((country) => (
                            <CountryOption
                              key={`priority-${country.code}`}
                              country={country}
                              isSelected={selectedCountry === country.code}
                              onSelect={handleCountrySelect}
                            />
                          ))}
                          <div className="border-t border-slate-600 my-1" />
                          <div className="px-3 py-1.5 text-xs text-slate-500 font-medium">
                            Alle Länder
                          </div>
                        </>
                      )}

                      {/* All countries (filtered) */}
                      {(showPriorityDivider
                        ? ALL_COUNTRY_CODES.filter(
                            (c) => !PRIORITY_COUNTRY_CODES.some((p) => p.code === c.code)
                          )
                        : filteredCountries
                      ).map((country) => (
                        <CountryOption
                          key={country.code}
                          country={country}
                          isSelected={selectedCountry === country.code}
                          onSelect={handleCountrySelect}
                        />
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Phone Number Input */}
          <input
            ref={ref}
            id={inputId}
            type="tel"
            value={localNumber}
            onChange={handlePhoneChange}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="one-time-code"
            data-form-type="other"
            className={cn(
              'flex-1 px-4 h-11 rounded-r-xl text-sm',
              'bg-slate-800/80 border border-slate-600/50 border-l-0 text-slate-100',
              'placeholder:text-slate-500',
              'focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 focus:bg-slate-800',
              'hover:border-slate-500/50 transition-all duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              error && 'border-red-500/50 focus:ring-red-500/50'
            )}
          />
        </div>

        {error && <p className="mt-1.5 text-sm text-red-400">{error}</p>}
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

/**
 * Country option component for the dropdown
 */
function CountryOption({
  country,
  isSelected,
  onSelect,
}: {
  country: CountryCode;
  isSelected: boolean;
  onSelect: (country: CountryCode) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(country)}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left',
        'hover:bg-slate-700/80 transition-all duration-200',
        isSelected && 'bg-slate-700/80'
      )}
    >
      <span className="w-7 text-center font-medium text-slate-400 text-xs">{country.code}</span>
      <span className="flex-1 text-slate-200">{country.name}</span>
      <span className="text-slate-400 tabular-nums">{country.dialCode}</span>
    </button>
  );
}
